import React, { useRef, useState } from "react";
import { Save, Loader2, Upload, FileText, Wand2 } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { useToast } from "../../hooks/use-toast";
import VideoUpload from "./VideoUpload";
import ImageUpload from "./ImageUpload";
import { toVtt, parseCaptions, captionsToTranscript } from "../../lib/captions";

const input = "w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-navy focus:outline-none focus:border-orange";

export default function AskCassieVideoIntro({ config, onSaved }) {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    enabled: false, heading: "Meet Cassie", subheading: "", video: "", poster: "",
    captions: "", transcript: "", autoplay: true,
    ...(config?.video_intro || {}),
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cues = parseCaptions(form.captions);

  const pickCaptionFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    const text = await file.text();
    set("captions", toVtt(text));
    toast({ title: "Caption file loaded", description: `${parseCaptions(toVtt(text)).length} caption lines — remember to save.` });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, captions: toVtt(form.captions) };
      if (!payload.video) payload.enabled = false;
      const cfg = await adminApi.askCassie.setConfig({ video_intro: payload });
      setForm({ ...form, ...(cfg.video_intro || {}) });
      onSaved?.(cfg);
      toast({ title: "Video intro saved", description: payload.enabled ? "It's live on the Ask Cassie hub." : "Saved — the panel is switched off." });
    } catch {
      toast({ title: "Couldn't save the video intro", variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="ask-cassie-video-intro">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-navy">Cassie video intro</h3>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-3xl">
              A short avatar clip at the top of the Ask Cassie hub. It plays once per visitor per session, muted with
              captions, and one tap turns the sound on. Upload the finished render here — no code change needed to swap it.
            </p>
          </div>
          <Button onClick={save} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full" data-testid="video-intro-save">
            {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Save size={15} className="mr-1.5" />} Save
          </Button>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <VideoUpload
            label="Intro video"
            value={form.video}
            onChange={(v) => set("video", v)}
            hint="The finished avatar render — 20–40 seconds works best. Max 300MB."
          />
          <ImageUpload
            label="Poster image (optional)"
            value={form.poster}
            onChange={(v) => set("poster", v)}
            hint="Shown before playback. Leave blank to use the video's first frame."
          />
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-semibold text-navy">Heading</span>
            <input className={`${input} mt-1`} value={form.heading} onChange={(e) => set("heading", e.target.value)} data-testid="video-intro-heading" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-navy">Supporting line</span>
            <input className={`${input} mt-1`} value={form.subheading} onChange={(e) => set("subheading", e.target.value)} data-testid="video-intro-subheading" />
          </label>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-navy">Show the panel</p>
              <p className="text-xs text-slate-400">Needs a video uploaded first.</p>
            </div>
            <Switch checked={!!form.enabled && !!form.video} disabled={!form.video} onCheckedChange={(v) => set("enabled", v)} data-testid="video-intro-enabled" />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-navy">Start automatically</p>
              <p className="text-xs text-slate-400">Muted, once per session. Off = wait for a click.</p>
            </div>
            <Switch checked={form.autoplay !== false} onCheckedChange={(v) => set("autoplay", v)} data-testid="video-intro-autoplay" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <h3 className="font-display font-bold text-navy">Captions & transcript</h3>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-3xl">
          Captions are on screen from the first frame, so the message lands without sound. Upload a caption file
          (.vtt or .srt) or paste one in. The transcript is rendered as page text under the video — accessible, and
          readable by Google.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept=".vtt,.srt,text/vtt,text/plain" onChange={pickCaptionFile} className="hidden" data-testid="video-intro-caption-file" />
          <Button onClick={() => fileRef.current?.click()} variant="outline" className="rounded-full" data-testid="video-intro-caption-upload">
            <Upload size={14} className="mr-1.5" /> Upload caption file
          </Button>
          <Button
            onClick={() => set("transcript", captionsToTranscript(form.captions))}
            disabled={!cues.length}
            variant="outline"
            className="rounded-full"
            data-testid="video-intro-fill-transcript"
          >
            <Wand2 size={14} className="mr-1.5" /> Build transcript from captions
          </Button>
          <span className="text-xs text-slate-400 flex items-center gap-1.5">
            <FileText size={13} /> {cues.length} caption line{cues.length === 1 ? "" : "s"} detected
          </span>
        </div>

        <label className="block mt-5">
          <span className="text-xs font-semibold text-navy">Caption file contents (WebVTT or SRT)</span>
          <textarea
            rows={10}
            value={form.captions}
            onChange={(e) => set("captions", e.target.value)}
            placeholder={"WEBVTT\n\n00:00:00.000 --> 00:00:03.500\nHi, I'm Cassie."}
            className={`${input} mt-1 font-mono text-xs`}
            data-testid="video-intro-captions"
          />
        </label>

        <label className="block mt-4">
          <span className="text-xs font-semibold text-navy">Transcript (shown under the video)</span>
          <textarea
            rows={5}
            value={form.transcript}
            onChange={(e) => set("transcript", e.target.value)}
            placeholder="Leave blank to use the caption lines joined together."
            className={`${input} mt-1`}
            data-testid="video-intro-transcript"
          />
        </label>

        {cues.length > 0 && (
          <div className="mt-5 rounded-xl bg-slate-50 p-4 max-h-60 overflow-y-auto" data-testid="video-intro-caption-preview">
            {cues.map((c, i) => (
              <p key={i} className="text-xs text-slate-600 py-1 border-b border-slate-100 last:border-0">
                <span className="font-mono text-[11px] text-slate-400 mr-2">{c.start.toFixed(1)}s</span>{c.text}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
